import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import BasicModal from "@/Modals/BasicModal";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";
import { termsMasterDataApi } from "@/apis/apis";
import BasicInput from "@/components/BasicInput";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import { TermInitialValues } from "@/data/WebDataManagement/MasterData/Terms/initial-values";
import TermSchema from "@/schema/WebDataManagement/MasterData/Terms/TermSchema";
import { useFormik } from "formik";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";

const TermModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${termsMasterDataApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Term ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${termsMasterDataApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: termsMasterDataApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: TermInitialValues(modalState.id ? showData : null),
    validationSchema: TermSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const payload = {
      code: values.code,
      name: values.name,
      status: values.status,
    };
    modalState.id
      ? saveEdit(JSON.stringify(payload))
      : saveCreate(JSON.stringify(payload));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = () => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  };

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Term" : "Add Term"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Term"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default TermModal;
